import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { formatTitle } from '../utils/common';
import LucideIcon from './LucideIcon';
import type { MenuItem } from '../modules/menu/model';

const getValidRoute = (route?: string | null) => {
  if (!route) return null;
  const trimmed = route.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('//')) {
    return `/${trimmed.replace(/^\/+/, '')}`;
  }

  if (trimmed.startsWith('/')) {
    const normalized = trimmed.replace(/\/{2,}/g, '/');
    return normalized.length > 1 ? normalized.replace(/\/+$/, '') : normalized;
  }

  try {
    if (/^https?:\/\//i.test(trimmed)) {
      const url = new URL(trimmed);
      if (url.hash?.startsWith('#/')) {
        const hashPath = url.hash.slice(1);
        return hashPath.length > 1 ? hashPath.replace(/\/+$/, '') : hashPath;
      }
      const path = `${url.pathname}${url.search}${url.hash}`.trim();
      const normalized = path.startsWith('/') ? path : `/${path}`;
      return normalized.length > 1
        ? normalized.replace(/\/+$/, '')
        : normalized;
    }
  } catch {
    // ignore invalid URL parsing
  }

  const fallback = `/${trimmed}`;
  return fallback.length > 1 ? fallback.replace(/\/+$/, '') : fallback;
};

interface FloatingSubmenuProps {
  menu: MenuItem;
  position: { top: number; left: number } | null;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  isActiveLink: (path: string) => boolean;
}

const FloatingSubmenu: React.FC<FloatingSubmenuProps> = ({
  menu,
  position,
  onMouseEnter,
  onMouseLeave,
  isActiveLink,
}) => {
  const [style, setStyle] = useState<React.CSSProperties>({});
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (position && menu) {
      // Calculate optimized position
      const windowHeight = window.innerHeight;
      const menuHeightEstimate = Math.min(
        (menu.submenus?.length || 0) * 40 + 20,
        300
      ); // Rough estimate

      let top = position.top;

      // If menu would go off bottom, shift it up
      if (top + menuHeightEstimate > windowHeight) {
        top = windowHeight - menuHeightEstimate - 20;
      }

      // If shift sent it off top, pin to top
      if (top < 10) {
        top = 10;
      }

      setStyle({
        top: `${top}px`,
        left: `${position.left}px`, // Flush with sidebar, use padding for gap
      });

      // Small timeout to trigger animation
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [position, menu?.submenus]);

  if (!position) return null;

  return (
    <div
      className={`fixed z-50 w-60 pl-3 bg-transparent transition-all duration-200 ease-out origin-left
        ${
          isVisible
            ? 'opacity-100 translate-x-0'
            : 'opacity-0 -translate-x-2 pointer-events-none'
        }
      `}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="relative w-full bg-white dark:bg-neutral-800 rounded-lg shadow-xl border border-neutral-200 dark:border-neutral-700">
        {/* Arrow */}
        <div className="absolute top-4 -left-1.5 w-3 h-3 bg-white dark:bg-neutral-800 border-l border-b border-neutral-200 dark:border-neutral-700 transform rotate-45" />

        <div className="py-2 px-1 relative">
          <div className="px-3 py-2 border-b border-neutral-100 dark:border-neutral-700 mb-1">
            <span className="text-sm font-semibold text-neutral-900 dark:text-white block truncate">
              {menu ? formatTitle(menu.menuName) : ''}
            </span>
          </div>

          <div className="max-h-[60vh] overflow-y-auto custom-scroll px-1 space-y-0.5">
            {menu?.submenus?.map((subMenu: MenuItem) => {
              const subLabel = formatTitle(subMenu.menuName);
              const subRoute = getValidRoute(subMenu.routePath);
              const isActive = subRoute ? isActiveLink(subRoute) : false;
              const subIcon = (subMenu.icon ?? '').trim();

              return subRoute ? (
                <NavLink
                  key={subMenu.menuId}
                  to={subRoute}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors focus:outline-none ${
                    isActive
                      ? 'bg-brand-50 dark:bg-brand-950 text-brand-600 dark:text-brand-400 font-medium'
                      : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-900 hover:text-neutral-900 dark:hover:text-white'
                  }`}
                >
                  {subIcon ? (
                    <LucideIcon
                      iconName={subIcon}
                      className="w-4 h-4 opacity-70 text-current"
                    />
                  ) : (
                    <div className="w-1.5 h-1.5 rounded-full bg-current opacity-40" />
                  )}
                  <span className="truncate">{subLabel}</span>
                </NavLink>
              ) : (
                <div
                  key={subMenu.menuId}
                  className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-neutral-400 dark:text-neutral-600 cursor-not-allowed"
                >
                  {subIcon ? (
                    <LucideIcon
                      iconName={subIcon}
                      className="w-4 h-4 opacity-70 text-current"
                    />
                  ) : (
                    <div className="w-1.5 h-1.5 rounded-full bg-current opacity-40" />
                  )}
                  <span className="truncate">{subLabel}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloatingSubmenu;
